/**
 * Realtime events (Technical Specification §8). Server → admin clients in room
 * `org:{orgId}:session:{sessionId}` / `org:{orgId}:admins`; server → tester in `tester:{testerId}`.
 */
import type { ExecutionStatus, FeedbackStatus, FeedbackType, Severity } from './enums';

export interface PresenceEvent {
  sessionId: string;
  testerId: string;
  name: string;
  sessionCaseId: string | null;
  stepIndex: number | null;
  recording: boolean;
  online: boolean;
  at: string;
}

export interface ExecutionUpdatedEvent {
  sessionId: string;
  testerId: string;
  sessionCaseId: string;
  status: ExecutionStatus;
  at: string;
}

export interface FeedbackCreatedEvent {
  sessionId: string;
  systemId: string;
  feedbackId: string;
  code: string;
  type: FeedbackType;
  title: string;
  severity: Severity | null;
  testerName: string;
  caseCode: string | null;
  duplicateOfCode: string | null;
  at: string;
}

export interface FeedbackUpdatedEvent {
  sessionId: string;
  feedbackId: string;
  status: FeedbackStatus;
  at: string;
}

export interface TesterMessageEvent {
  kind: 'nudge' | 'info_requested' | 'retest_created' | 'session_status';
  message: string;
  feedbackId?: string;
  at: string;
}

export interface NotificationEvent {
  id: string;
  kind: string;
  title: string;
  link: string;
  at: string;
}

export interface ServerToAdminEvents {
  'tester.presence': (e: PresenceEvent) => void;
  'execution.updated': (e: ExecutionUpdatedEvent) => void;
  'feedback.created': (e: FeedbackCreatedEvent) => void;
  'feedback.updated': (e: FeedbackUpdatedEvent) => void;
  'notification.created': (e: NotificationEvent) => void;
  'session.updated': (e: { sessionId: string; at: string }) => void;
}

export interface ServerToTesterEvents {
  'tester.message': (e: TesterMessageEvent) => void;
}

export interface AdminToServerEvents {
  'session.watch': (sessionId: string) => void;
  'session.unwatch': (sessionId: string) => void;
}

export const REALTIME_PATH = '/socket.io';
export const ADMIN_NAMESPACE = '/admin';
export const TESTER_NAMESPACE = '/tester';
